import React from "react";
import { useTheme, styled } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

import { Menu as MenuIcon } from "@mui/icons-material";

import { Link as RouterLink } from "react-router";

import { useData } from "../contexts/DataContext";
import { useApp } from "../contexts/AppContext";

import { employeeDashboardOrder, sectionOrder } from "../utils/consts";
import {
  countDashboardsWithChildren,
  formatDashboardCountLabel,
} from "../utils/dashboardCount";
import DashboardChildNavList from "./DashboardChildNavList";
import type { DashboardInfo } from "../utils/types";
import { colors } from "../utils/colors";

const drawerWidth = 300;
const buttonWidth = 48;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: open ? 0 : `-${drawerWidth - buttonWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function PersistentDrawerLeft({
  children,
}: {
  readonly children: any;
}) {
  const theme = useTheme();

  const { dashboardList } = useData();
  const { drawerOpen, handleDrawerOpen, handleDrawerClose } = useApp();

  const handleDrawerToggle = () => {
    if (drawerOpen) {
      handleDrawerClose();
    } else {
      handleDrawerOpen();
    }
  };

  const [expanded, setExpanded] = React.useState<string | false>(false);

  const toggleSection =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const dashboardsBySection: any = dashboardList.reduce(
    (acc: any, dashboard: any) => {
      acc[dashboard.section] ??= [];
      acc[dashboard.section].push(dashboard);
      return acc;
    },
    {},
  );

  type EmployeeDashboardOrderKey = keyof typeof employeeDashboardOrder;

  Object.keys(dashboardsBySection).forEach((section) => {
    dashboardsBySection[section].sort((a: any, b: any) => {
      if (section === "Employee Dashboard") {
        const orderA =
          employeeDashboardOrder[a.title as EmployeeDashboardOrderKey] ?? 999;
        const orderB =
          employeeDashboardOrder[b.title as EmployeeDashboardOrderKey] ?? 999;
        return orderA - orderB;
      }
      const idxA = sectionOrder.indexOf(a.title as string);
      const idxB = sectionOrder.indexOf(b.title as string);
      return (
        (idxA === -1 ? sectionOrder.length : idxA) -
        (idxB === -1 ? sectionOrder.length : idxB)
      );
    });
  });

  const renderDashboardButton = (dashboard: DashboardInfo) => {
    return (
      <Button
        component={RouterLink}
        to={`/dashboard/${dashboard.id}`}
        variant="text"
        key={dashboard.id}
        onClick={handleDrawerClose}
        sx={{
          flexDirection: "column",
          textTransform: "capitalize",
          alignItems: "flex-start",
          textAlign: "left",
          width: "100%",
          minHeight: "48px",
          paddingLeft: "16px",
          paddingTop: "12px",
          paddingBottom: "12px",
          border: "none",
          borderRadius: "0px",
          backgroundColor: "#E4E4E4",
          color: theme.palette.text.primary,
          textDecoration: "none",
          ":hover": {
            bgcolor: colors.dashboard_nav_child_surface,
          },
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: "400" }}>
          {dashboard.title}
        </Typography>
      </Button>
    );
  };

  const renderAccordion = ([section, sectionDashboards]: any, idx: number) => {
    const sectionDashboardCount =
      countDashboardsWithChildren(sectionDashboards);

    return (
      <Accordion
        key={section}
        expanded={expanded === `panel${idx}`}
        onChange={toggleSection(`panel${idx}`)}
        disableGutters={true}
        style={{ marginBottom: "5px", border: "none", boxShadow: "none" }}
      >
        <AccordionSummary
          expandIcon={
            `panel${idx}` ? (
              <img src="images/down-arrow.svg" alt="less" />
            ) : (
              <img src="images/up-arrow.svg" alt="more" />
            )
          }
          aria-controls={`panel${idx}bh-content`}
          id={`panel${idx}bh-header`}
          style={{ marginBottom: "5px", border: "none", height: "68px" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                width: "100%",
                flexShrink: 0,
                fontSize: "16px",
                fontWeight: "600",
              }}
              variant="h6"
            >
              {section}
            </Typography>
            <Typography
              sx={{
                width: "100%",
                flexShrink: 0,
                fontSize: "14px",
                fontWeight: "500",
              }}
              variant="subtitle1"
            >
              {formatDashboardCountLabel(sectionDashboardCount)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails style={{ padding: "0px" }}>
          {sectionDashboards.map((dashboard: DashboardInfo) => (
            <div key={dashboard.id}>
              <Box
                sx={{ height: "4px", backgroundColor: "#f8f7fc", flexGrow: 1 }}
              />
              {renderDashboardButton(dashboard)}
              <DashboardChildNavList
                childDashboards={dashboard.child_dashboards}
                parentSection={dashboard.section}
                onNavigate={handleDrawerClose}
              />
            </div>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const drawer = () => {
    return (
      <Box
        sx={{ position: "relative", display: "flex", alignItems: "flex-start" }}
      >
        <Box
          sx={{
            position: "absolute",
            left: drawerOpen ? "1.125rem" : "1.625rem",
            top: theme.spacing(1),
            zIndex: 2,
            transition: theme.transitions.create("left", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              boxShadow: 2,
              borderRadius: "0 4px 4px 0",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
            aria-label={drawerOpen ? "Collapse menu" : "Expand menu"}
          >
            <MenuIcon color="primary" />
          </IconButton>
        </Box>
        <Drawer
          sx={{
            display: "block",
            position: "relative",
            minHeight: "calc(100vh - 80px - 40px)",
            width: drawerWidth,
            flexShrink: 0,
            py: theme.spacing(2),
            zIndex: 1,
            ...(drawerOpen && { pl: theme.spacing(2) }),
            ...(!drawerOpen && { pointerEvents: "none" }),
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
              position: "relative",
              backgroundColor: "#f8f7fc",
              overflowX: "hidden",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                boxShadow: `0px 2px 2px 0px rgba(0, 0, 0, 0.06) inset`,
                backgroundColor: "#DEDEDE",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#BEBEBE",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#BEBEBE",
              },
            },
          }}
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerClose}
        >
          <Box sx={{ width: drawerWidth, paddingTop: "3rem" }}>
            {Object.entries(dashboardsBySection)
              .sort(([sectionA], [sectionB]) => {
                const idxA = sectionOrder.indexOf(sectionA);
                const idxB = sectionOrder.indexOf(sectionB);
                return (
                  (idxA === -1 ? sectionOrder.length : idxA) -
                  (idxB === -1 ? sectionOrder.length : idxB)
                );
              })
              .map(renderAccordion)}
          </Box>
        </Drawer>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      {drawer()}
      <Main open={drawerOpen}>{children}</Main>
    </Box>
  );
}
